import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import FarmersNav from "@/components/FarmersNav";
import {
  ArrowLeft, ArrowRight, Storefront, Plant, Sparkle, Globe, Eye, PencilSimple,
  Rocket, Warning, MagicWand,
} from "@phosphor-icons/react";

/**
 * FarmersWebsitesList — /farmers/websites
 *
 * Lists my farm sites. Backend enforces one active site per owner —
 * this UI reflects that: if I have one active site, the CTA becomes
 * "Open editor"; otherwise it's "Create My Farm Website".
 */
export default function FarmersWebsitesList() {
  const { user, requireSignIn } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    http.get("/farmers/websites/mine")
      .then(({ data }) => setSites(data.websites || []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <Storefront size={40} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <h1 className="mt-4 font-serif-display text-4xl">Farm sites live in your account</h1>
          <p className="mt-2 text-[#3A3830]">Sign in to launch your farm website.</p>
          <button onClick={requireSignIn}
            className="mt-6 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 text-sm"
            data-testid="sites-signin-cta">Sign in to continue</button>
        </div>
      </main>
    );
  }

  const active = sites.filter((s) => ["draft", "published", "closed"].includes(s.status));

  return (
    <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 pb-20 px-5 md:px-10" data-testid="farmers-websites-list">
      <FarmersNav />
      <div className="max-w-5xl mx-auto">
        <Link to="/farmers/mine" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> My Farm
        </Link>
        <div className="mt-3 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">
              <Sparkle size={12} weight="fill" className="inline mr-1" /> Farm Website Builder
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              Your farm on the open web.
            </h1>
            <p className="mt-3 max-w-2xl text-[#3A3830]">
              Every farm has a story. Build a modular website that shows your produce, deals, farm stand, journal, and location — in minutes. Themes are presentation only; your content stays yours.
            </p>
          </div>
          <div>
            {active.length === 0 ? (
              <Link to="/farmers/websites/new"
                className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 text-sm font-medium"
                data-testid="sites-create-cta">
                <Rocket size={13} weight="fill" /> Create my farm website
              </Link>
            ) : (
              <Link to={`/farmers/websites/${active[0].id}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 text-sm font-medium"
                data-testid="sites-open-editor-cta">
                <PencilSimple size={13} weight="fill" /> Open editor
              </Link>
            )}
          </div>
        </div>

        {active.length === 0 && (
          <section className="mt-8 rounded-3xl bg-gradient-to-br from-[#1B4332] via-[#22201d] to-[#3a2a1c] text-white p-6 md:p-10 relative overflow-hidden" data-testid="sites-clone-banner">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#B85F2A]/25 blur-3xl" />
            <div className="relative grid md:grid-cols-[1.4fr,1fr] items-center gap-6">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F1B48A] flex items-center gap-1">
                  <MagicWand size={12} weight="fill" /> New · AI import
                </div>
                <h2 className="mt-2 font-serif-display text-3xl md:text-4xl leading-[1.02] tracking-tight">
                  Already have a farm site? Clone it in 30 seconds.
                </h2>
                <p className="mt-2 text-sm md:text-base text-white/80 max-w-lg">
                  Paste your existing site's URL. Our AI reads every page — farm name, story, products, contact — and rebuilds it as a modular Farmers Pulse site you can edit block-by-block.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Link to="/farmers/websites/import"
                    className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 text-sm font-medium"
                    data-testid="sites-import-cta">
                    <MagicWand size={13} weight="fill" /> Clone from URL <ArrowRight size={12} weight="bold" />
                  </Link>
                  <Link to="/farmers/websites/new"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm hover:bg-white/10"
                    data-testid="sites-manual-cta">
                    Or start from scratch
                  </Link>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/10 p-4 space-y-2 text-xs">
                  <div className="flex items-center gap-1.5"><Globe size={12} weight="fill" className="text-[#F1B48A]" /> Crawls up to 7 pages</div>
                  <div className="flex items-center gap-1.5"><MagicWand size={12} weight="fill" className="text-[#F1B48A]" /> AI extracts farm identity + products</div>
                  <div className="flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-[#F1B48A]" /> Preview live before you launch</div>
                  <div className="flex items-center gap-1.5"><PencilSimple size={12} weight="fill" className="text-[#F1B48A]" /> Everything is editable after</div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="mt-10">
          {loading ? (
            <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-sm text-[#3A3830]">
              Loading your sites…
            </div>
          ) : sites.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {sites.map((s) => <SiteCard key={s.id} site={s} />)}
            </div>
          )}
        </section>

        <section className="mt-12 grid md:grid-cols-3 gap-4">
          <BenefitCard icon={Plant} title="Modular blocks"
            body="Hero, products, fresh today, deals, journal, contact — reorder, hide or add without code." />
          <BenefitCard icon={Storefront} title="Content lives yours"
            body="Products, deals and photos live in your account. Switch themes anytime — content never moves." />
          <BenefitCard icon={Globe} title="Own your URL"
            body={"Publish at /f/your-farm. Custom domain coming in Phase 5."} />
        </section>
      </div>
    </main>
  );
}

function SiteCard({ site }) {
  const statusColor = {
    draft: { bg: "bg-amber-100", ink: "text-amber-800", dot: "bg-amber-500" },
    published: { bg: "bg-emerald-100", ink: "text-emerald-800", dot: "bg-emerald-500" },
    closed: { bg: "bg-neutral-100", ink: "text-neutral-700", dot: "bg-neutral-400" },
    archived: { bg: "bg-red-100", ink: "text-red-700", dot: "bg-red-500" },
  }[site.status] || { bg: "bg-neutral-100", ink: "text-neutral-700", dot: "bg-neutral-400" };
  return (
    <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid={`site-card-${site.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-[#3A3830]">{site.theme_id} · {site.starter_package}</div>
            <div className="mt-1 text-lg font-medium truncate">{site.farm_name}</div>
            {site.tagline && <div className="text-sm text-[#3A3830] truncate">{site.tagline}</div>}
          </div>
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-mono rounded-full px-2 py-0.5 ${statusColor.bg} ${statusColor.ink}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} /> {site.status}
          </span>
        </div>
        <div className="mt-4 flex items-center gap-1 text-xs text-[#3A3830]">
          <Globe size={11} weight="fill" className="text-[#B85F2A]" /> /f/{site.slug}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Link to={`/farmers/websites/${site.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#1B4332] text-[#F5EBDC] px-3.5 py-2 text-xs font-medium"
            data-testid={`site-card-edit-${site.id}`}>
            <PencilSimple size={11} weight="fill" /> Edit
          </Link>
          {site.status === "published" && (
            <Link to={`/f/${site.slug}`} target="_blank"
              className="inline-flex items-center gap-1 rounded-full border border-black/15 px-3.5 py-2 text-xs"
              data-testid={`site-card-view-${site.id}`}>
              <Eye size={11} weight="fill" /> View live
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-black/10 bg-white p-10 md:p-14 text-center" data-testid="sites-empty">
      <Storefront size={44} weight="duotone" className="mx-auto text-[#B85F2A]" />
      <h2 className="mt-4 font-serif-display text-2xl md:text-3xl">You haven't built a farm site yet.</h2>
      <p className="mt-2 text-sm text-[#3A3830] max-w-md mx-auto">
        Pick a starter pack + theme, tell us a little about your farm, and we'll spin up a draft site you can edit block-by-block.
      </p>
      <Link to="/farmers/websites/new"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 text-sm font-medium">
        <Rocket size={13} weight="fill" /> Start the setup wizard <ArrowRight size={13} weight="bold" />
      </Link>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5">
      <Icon size={20} weight="fill" className="text-[#B85F2A]" />
      <div className="mt-2 text-sm font-medium">{title}</div>
      <div className="mt-1 text-xs text-[#3A3830]">{body}</div>
    </div>
  );
}
